/**
 * The batch runner — ADR-035 §3.3, `⇪ Package all…`.
 *
 * Walks a roster sheet over a folder of bodies and drives the EXISTING
 * one-avatar door once per row: open through the load door, apply the row's
 * choices, play its selection clip and let the stage settle on its first
 * frames, take the headshot, package, write `<id>/`. One body in flight; a
 * refusal is a report row, never a stop; a cancel finishes the body in
 * flight and stops. The filled sheet comes back for the write-back — the id
 * and the clip a run derived must never drift ([[avatar-packaging-arc]]).
 *
 * Pure in shape: every step is INJECTED (`BatchDeps`), so the loop is
 * testable with fakes and the smoke drives it with the real seams. Nothing
 * here touches the DOM, the viewport, or the store directly.
 *
 * Depends on: kernel (roster-sheet, library types), package-folder types,
 * library-read types. Depended on by: main (the door), tests, the smoke.
 */
import {
  choicesForRow,
  fillRow,
  reconcileSheet,
  withFilledRow,
  PackageRefusal,
  type FilledField,
  type PackageChoices,
  type Roster,
  type RosterSheet,
  type RosterSheetRow,
} from '@harryhausen/kernel';
import type { FolderFileLike } from './library-read.js';
import type { PlayOutcome } from './library-player.js';
import type { PackagePlan } from './package-folder.js';

export interface BatchDeps {
  /** sha256 hex of a body's bytes — the seed of every deterministic fill. */
  readonly sha256: (bytes: ArrayBuffer) => Promise<string>;
  /** The live roster as read (null = unreachable; ids then slugify, nothing replaces). */
  readonly roster: () => Roster | null;
  /** The load door: the body becomes the subject. false = superseded or refused (the door said why on its own line). */
  readonly open: (file: FolderFileLike, bytes: ArrayBuffer) => Promise<boolean>;
  /** Commit the row's choices to the Document (package.set). */
  readonly setChoices: (choices: PackageChoices) => void;
  /** Play a library clip on the subject (the phase-4 door through the player); null stops the preview. */
  readonly play: (clipId: string | null) => Promise<PlayOutcome>;
  /**
   * True when the family's clips reach this body only through its PACKAGE:
   * a rig preset renamed and re-seated by `canonicalizeForFamily` (a kobold's
   * `BoneRoot`). The stage cannot preview there without showing a twist World
   * will not, so the row takes its headshot at rest instead of refusing — the
   * one-avatar door already packages such a body the same way.
   */
  readonly previewOnlyInPackage: () => boolean;
  /** Wait until the stage has rendered the played clip's first frames — a paused mixer shows the REST pose, so the take is made a frame or two in. */
  readonly settle: () => Promise<void>;
  /** The headshot take (ADR-035 phase 1); returns the take's name, null when refused. */
  readonly takeHeadshot: () => Promise<string | null>;
  /** The one-avatar door's plan (readiness gate included). */
  readonly packageToPlan: () => Promise<PackagePlan>;
  /** Write a plan where the batch was pointed; returns where it landed. */
  readonly write: (plan: PackagePlan) => Promise<string>;
  /** Asked between bodies. */
  readonly cancelled: () => boolean;
  readonly now?: () => string;
}

export interface BatchRow {
  readonly file: string;
  readonly name: string | null;
  readonly id: string | null;
  readonly selectionClip: string | null;
  readonly ok: boolean;
  /** Why the body did not package — every reason, in order. Empty when ok. */
  readonly reasons: readonly string[];
  /** Said, never refused (a picker family World has not seen yet). Present on ok rows too. */
  readonly warnings: readonly string[];
  /** Columns the fill wrote into the sheet for this row. */
  readonly filled: readonly FilledField[];
  /** The roster row this package replaces, when one. */
  readonly replaces: { readonly id: string; readonly name: string } | null;
  readonly hero: string | null;
  /** The headshot was taken at rest: the selection clip plays only on the packaged skeleton (`previewOnlyInPackage`). */
  readonly heroAtRest: boolean;
  readonly wrote: string | null;
}

export interface BatchReport {
  readonly schemaVersion: '1.0.0';
  readonly startedAt: string;
  readonly finishedAt: string;
  readonly source: string;
  readonly cancelled: boolean;
  readonly packaged: number;
  readonly refused: number;
  readonly skipped: readonly { readonly file: string; readonly reason: string }[];
  readonly bodiesWithoutRows: readonly string[];
  readonly rowsWithoutBodies: readonly string[];
  readonly rows: readonly BatchRow[];
}

export interface BatchInputs {
  readonly sheet: RosterSheet;
  /** The source folder's name, for the report. */
  readonly source: string;
  /** The folder's files (bodies and anything else — non-GLBs are ignored here). */
  readonly files: readonly FolderFileLike[];
  readonly deps: BatchDeps;
  /** Called as each row finishes — the status line and the panel read this. */
  readonly onRow?: (row: BatchRow, index: number, total: number) => void;
}

const isGlb = (f: FolderFileLike): boolean => /\.glb$/i.test(f.name);

/** The status line's account of a batch as it goes and when it ends. */
export function batchLine(done: number, total: number, refused: number, cancelled: boolean): string {
  const state = cancelled ? 'cancelled' : done < total ? 'running' : 'done';
  return `package all: ${done}/${total}${refused > 0 ? ` · ${refused} refused` : ''} · ${state}`;
}

function reasonsOf(err: unknown): string[] {
  if (err instanceof PackageRefusal) return [...err.reasons];
  return [err instanceof Error ? err.message : String(err)];
}

/**
 * Run the sheet over the folder. Returns the report and the FILLED sheet.
 * Never throws for a body: every failure is a row with its reasons. Throws
 * only when the sheet and the folder cannot be reconciled at all (no GLB).
 */
export async function runBatch(inputs: BatchInputs): Promise<{ readonly report: BatchReport; readonly sheet: RosterSheet }> {
  const { deps } = inputs;
  const now = deps.now ?? ((): string => new Date().toISOString());
  const startedAt = now();
  const glbs = inputs.files.filter(isGlb);
  const byName = new Map(glbs.map((f) => [f.name.toLowerCase(), f]));
  const rec = reconcileSheet(inputs.sheet, glbs.map((f) => f.name));
  let sheet = inputs.sheet;
  const rows: BatchRow[] = [];
  let cancelled = false;
  const total = rec.packageable.length;
  const rosterNames = (deps.roster()?.rows ?? []).map((r) => r.name);

  for (let i = 0; i < rec.packageable.length; i += 1) {
    if (deps.cancelled()) {
      cancelled = true;
      break;
    }
    const row = rec.packageable[i] as RosterSheetRow;
    const file = byName.get(row.file.toLowerCase()) as FolderFileLike;
    const finish = (r: BatchRow): void => {
      rows.push(r);
      inputs.onRow?.(r, rows.length, total);
    };
    let filledRow: RosterSheetRow = row;
    let filled: readonly FilledField[] = [];
    let replaces: BatchRow['replaces'] = null;
    let warnings: readonly string[] = [];
    let choices: PackageChoices | null = null;
    let hero: string | null = null;
    let atRest = false;
    try {
      if (file.arrayBuffer === undefined) throw new Error(`"${row.file}" offers no bytes — the folder seam handed a text-only file`);
      const bytes = await file.arrayBuffer();
      const sha = await deps.sha256(bytes);
      // Names every OTHER row claims (as filled so far) plus the roster's — a filled name never collides.
      const taken = [...sheet.rows.filter((r) => r.file.toLowerCase() !== row.file.toLowerCase() && r.skip === undefined && r.name !== undefined).map((r) => r.name as string), ...rosterNames];
      const takenIds = sheet.rows.filter((r) => r.file.toLowerCase() !== row.file.toLowerCase() && r.skip === undefined && r.id !== undefined).map((r) => r.id as string);
      const f = fillRow(sheet, row, { sha256: sha, roster: deps.roster(), takenNames: taken, takenIds });
      filledRow = f.row;
      filled = f.filled;
      replaces = f.replaces;
      warnings = f.warnings;
      // What the fill DERIVED is written back even when it also refused (a
      // name and an id but no clip, the pool empty): the next run then reads
      // the name rather than re-deriving it against a changed set of taken
      // names (Codex P2, #162 round four). A refused column stays unset.
      sheet = withFilledRow(sheet, filledRow);
      if (f.refusals.length > 0) throw new PackageRefusal(f.refusals);
      choices = choicesForRow(sheet, filledRow);
      const opened = await deps.open(file, bytes);
      if (!opened) throw new Error(`"${row.file}" did not become the subject — the load door refused or a newer gesture overtook it`);
      // The replaced row's picker facets carry at the one-avatar door's compose
      // (packageToPlan → the panel's identity.replaces), not here: a row that
      // leaves a facet unset keeps prod's value, and the sheet never copies it.
      deps.setChoices(choices);
      atRest = deps.previewOnlyInPackage();
      if (atRest) {
        // Clear the previous row's clip so the take is this body's rest, not a stale pose.
        await deps.play(null);
      } else {
        const outcome = await deps.play(choices.selectionClip as string);
        if (outcome !== 'played') throw new Error(`selection clip "${choices.selectionClip}" did not play (${outcome})`);
      }
      await deps.settle();
      hero = await deps.takeHeadshot();
      if (hero === null) throw new Error('the headshot was refused — see the status line');
      const plan = await deps.packageToPlan();
      const wrote = await deps.write(plan);
      finish({ file: row.file, name: filledRow.name ?? null, id: filledRow.id ?? null, selectionClip: filledRow.selectionClip ?? null, ok: true, reasons: [], warnings, filled, replaces, hero, heroAtRest: atRest, wrote });
    } catch (err) {
      finish({ file: row.file, name: filledRow.name ?? null, id: filledRow.id ?? null, selectionClip: filledRow.selectionClip ?? null, ok: false, reasons: reasonsOf(err), warnings, filled, replaces, hero, heroAtRest: atRest, wrote: null });
    }
  }
  const report: BatchReport = {
    schemaVersion: '1.0.0',
    startedAt,
    finishedAt: now(),
    source: inputs.source,
    cancelled,
    packaged: rows.filter((r) => r.ok).length,
    refused: rows.filter((r) => !r.ok).length,
    skipped: rec.skipped,
    bodiesWithoutRows: rec.bodiesWithoutRows,
    rowsWithoutBodies: rec.rowsWithoutBodies,
    rows,
  };
  return { report, sheet };
}
